import React, { createContext, useContext, useMemo, useCallback, ReactNode } from 'react'
import * as RNLocalize from 'react-native-localize'
import { createIntl, createIntlCache, IntlShape } from '@formatjs/intl'
import { enTranslations, frTranslations, esTranslations } from '@translations'
import { useLanguageStore } from '../store/useLanguageStore'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import 'dayjs/locale/es'

type TranslationMessages = {
  [key: string]: string | TranslationMessages
}

export type AvailableLanguages = 'en' | 'fr' | 'es'

export type Language = 'auto' | AvailableLanguages

interface LanguageContextProps {
  t: (key: string, params?: Parameters<IntlShape['formatMessage']>[1]) => string
  currentLanguage: AvailableLanguages
}

const translations: Record<AvailableLanguages, TranslationMessages> = {
  en: enTranslations,
  fr: frTranslations,
  es: esTranslations,
}

interface LanguageProviderProps {
  children: ReactNode
}

const LanguageContext = createContext<LanguageContextProps>({
  t: () => '',
  currentLanguage: 'fr',
})

export const useLanguage = () => useContext(LanguageContext)

function flattenMessages(nestedMessages: TranslationMessages, prefix = ''): Record<string, string> {
  return Object.entries(nestedMessages).reduce<Record<string, string>>(
    (flatMessages, [key, value]) => {
      const prefixedKey = prefix ? `${prefix}.${key}` : key
      if (typeof value === 'string') {
        flatMessages[prefixedKey] = value
      } else {
        Object.assign(flatMessages, flattenMessages(value, prefixedKey))
      }
      return flatMessages
    },
    {},
  )
}

export const resolveLanguage = (language: Language): AvailableLanguages => {
  if (language === 'auto') {
    const autoLang = RNLocalize.getLocales()[0].languageTag.split('-')[0] as AvailableLanguages
    return Object.prototype.hasOwnProperty.call(translations, autoLang) ? autoLang : 'fr'
  }
  return language
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { language } = useLanguageStore()

  const currentLanguage = resolveLanguage(language)

  dayjs.locale(currentLanguage)

  const messages = useMemo(() => flattenMessages(translations[currentLanguage]), [currentLanguage])
  const intlCache = useMemo(() => createIntlCache(), [])
  const intl = useMemo<IntlShape>(
    () => createIntl({ locale: currentLanguage, messages, defaultLocale: 'fr' }, intlCache),
    [currentLanguage, messages, intlCache],
  )

  const t = useCallback(
    (key: string, params?: Parameters<IntlShape['formatMessage']>[1]) =>
      intl.formatMessage({ id: key }, params).toString(),
    [intl],
  )

  return <LanguageContext.Provider value={{ t, currentLanguage }}>{children}</LanguageContext.Provider>
}

export default LanguageContext
